import { FC, useEffect } from 'react';
import { History } from '@mui/icons-material';
import { Pagination, Stack, Typography } from '@mui/material';
import isEmpty from 'lodash/isEmpty';

import { usePagination } from '@cvt/hooks/usePagination';
import { useDictionary } from '@cvt/hooks/useDictionary';
import { DEFAULT_QUERY_LIMIT } from '@cvt/helpers/query';
import { MessageFeedbackView } from '@cvt/components/MessageFeedbackView';

import { BoxWell } from '@shared/components/BoxWell';

import { useUserPreferenceHistory } from '@modules/Users/hooks/useUserPreferenceHistory';
import { PreferenceHistoryItem } from '@modules/Users/components/PreferenceHistoryItem';


interface Props {
  description: string;
}

export const PreferenceVersionHistory: FC<Props> = ({ description }) => {
  const dictionary = useDictionary();
  const { totalPages, setCount, page, setPage, limit, offset } = usePagination(DEFAULT_QUERY_LIMIT);
  const { preferenceHistory, count, status } = useUserPreferenceHistory({ limit, offset });

  useEffect(() => {
    if (status === 'success') {
      setCount(count);
    }
  }, [status, setCount, count]);

  return (
    <BoxWell sx={{ px: { xs: 0, sm: 3 }, borderColor: { xs: 'transparent', sm: 'divider' }, py: 2 }}>
      <Stack direction="row" alignItems="center" gap={1}>
        <History/>
        <Typography variant="body1" fontWeight={600} fontSize={{ xs: 20, sm: 30 }}>
          {dictionary.preferences.versionHistory.title}
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" mt={3}>
        {description}
      </Typography>

      {!isEmpty(preferenceHistory) ? (
        <Stack spacing={2} mt={3}>
          {preferenceHistory.map((item, index) => (
            <PreferenceHistoryItem
              key={item.historyId}
              version={`V${count - (index + (page - 1) * limit)}`}
              {...item}
            />
          ))}
        </Stack>
      ) : (
        <MessageFeedbackView height={100} message={dictionary.preferences.versionHistory.noHistory}/>
      )}

      {count > limit && (
        <Stack width="100%" p={2} mt="auto" justifyContent="center">
          <Pagination
            sx={{ display: 'flex', justifyContent: 'center' }}
            count={totalPages}
            page={page}
            onChange={(event, value) => setPage(value)}
            showFirstButton={page !== 1}
            showLastButton={page !== totalPages}
          />
        </Stack>
      )}
    </BoxWell>
  );
};